import { Moment } from 'moment';
import { User } from 'api/models';
import { Project, ProjectPhase } from 'api/project';
import { FieldValue, FieldValueType, Task } from 'api/tasks';
import { ProjectsWithMembers } from 'components/organisms/resource-management/types';
import { NormalizedProjectTemplatePayload } from 'data/templates/projects/api/transform';
import { ProjectTemplateProjectPhase } from 'data/templates/projects/types';
import { defaultFormat } from 'utils/date-field/formatter';
import { denormalizeAndCleanUpTemplate } from './template';

const asProjectPhases = (
  phases: ProjectTemplateProjectPhase[],
  relativeDate: Moment,
): ProjectPhase[] =>
  phases.map((phase) => {
    const startDate = relativeDate.add(phase.startDay, 'days');

    return {
      projectPhaseId: phase.projectPhaseId,
      projectPhaseName: phase.projectPhaseName,
      tasks: [] as Task[],
      fields: [] as FieldValue<FieldValueType>[],
      startDate: defaultFormat(startDate),
      dueDate: defaultFormat(
        startDate.add((phase.duration || 1) - 1, 'days').endOf('day'),
      ),
      private: phase.private,
    } as unknown as ProjectPhase;
  });

export const asProject = (
  projectTemplate: NormalizedProjectTemplatePayload,
  projectUsers: User[],
  relativeDate: Moment,
): Project => {
  const template = denormalizeAndCleanUpTemplate(projectTemplate);
  const startDate = relativeDate.add(template.templateContent.startDay, 'days');

  const project = {
    projectId: template.templateId === 'new' ? -1 : template.templateId,
    projectName: template.templateName,
    budgetedHours: template.templateContent.budgetedHours,
    startDate: defaultFormat(startDate),
    dueDate: defaultFormat(
      startDate
        .add((template.templateContent.duration || 1) - 1, 'days')
        .endOf('day'),
    ),
    teamMembers: projectUsers,
    projectPhases: [],
    projectOwner: {},
  } as unknown as Project;

  return {
    ...project,
    projectPhases: asProjectPhases(
      template.templateContent.projectPhases ?? [],
      relativeDate,
    ),
  };
};

export const asProjectWithMember = (project: Project): ProjectsWithMembers => ({
  ...project,
  projectMembers: (project.teamMembers ?? []).map((x) => ({ user: x })),
});
